import { DesignDialog as Dialog } from "src/shared/ui/src/design/DesignDialog";
import { useRef, useState } from "react";
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import dico from "../../shared/util/src/language/dico";
import {
  EntrySignatureDrawing,
  isEntrySignatureDrawing,
} from "../../shared/util/src/athlete-entry";

/** Static ink capture, with normalized coordinates only. */
export default function SignatureDialog({
  summary,
  onConfirm,
  onCancel,
}: {
  summary: string;
  onConfirm: (drawing: EntrySignatureDrawing) => void;
  onCancel: () => void;
}) {
  const t = dico().athleteEntry;
  const [drawing, setDrawing] = useState<EntrySignatureDrawing>([]);
  const strokes = useRef<EntrySignatureDrawing>([]);
  const active = useRef<number | null>(null);
  const point = (
    event: React.PointerEvent<HTMLDivElement>,
  ): [number, number] => {
    const rect = event.currentTarget.getBoundingClientRect();
    return [
      Math.round(
        Math.max(
          0,
          Math.min(1000, ((event.clientX - rect.left) / rect.width) * 1000),
        ),
      ),
      Math.round(
        Math.max(
          0,
          Math.min(1000, ((event.clientY - rect.top) / rect.height) * 1000),
        ),
      ),
    ];
  };
  const finish = () => {
    active.current = null;
    strokes.current = strokes.current.filter((stroke) => stroke.length >= 2);
    setDrawing([...strokes.current]);
  };
  return (
    <Dialog open fullWidth maxWidth="sm" onClose={onCancel}>
      <DialogTitle>{t.signatureTitle}</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Typography>{summary}</Typography>
          <Typography variant="body2">{t.signatureNotice}</Typography>
          <Box
            role="img"
            aria-label={t.signatureDraw}
            sx={{
              height: 180,
              border: 1,
              borderColor: "divider",
              borderRadius: 2,
              bgcolor: "#fff",
              touchAction: "none",
              position: "relative",
              cursor: "crosshair",
            }}
            onPointerDown={(event) => {
              if (active.current !== null || strokes.current.length >= 100)
                return;
              event.preventDefault();
              event.currentTarget.setPointerCapture(event.pointerId);
              active.current = event.pointerId;
              strokes.current.push([point(event)]);
            }}
            onPointerMove={(event) => {
              if (active.current !== event.pointerId) return;
              const stroke = strokes.current[strokes.current.length - 1];
              if (strokes.current.reduce((sum, s) => sum + s.length, 0) >= 3000)
                return;
              stroke.push(point(event));
              setDrawing(strokes.current.map((s) => [...s]));
            }}
            onPointerUp={finish}
            onPointerCancel={finish}
            onLostPointerCapture={finish}
          >
            <svg
              viewBox="0 0 1000 1000"
              preserveAspectRatio="none"
              width="100%"
              height="100%"
              style={{ pointerEvents: "none" }}
            >
              {drawing.map((stroke, index) => (
                <polyline
                  key={index}
                  points={stroke.map((p) => p.join(",")).join(" ")}
                  fill="none"
                  stroke="#111"
                  strokeWidth="5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              ))}
            </svg>
          </Box>
          <Button
            onClick={() => {
              strokes.current = [];
              setDrawing([]);
            }}
          >
            {t.signatureClear}
          </Button>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>{t.close}</Button>
        <Button
          variant="contained"
          disabled={!isEntrySignatureDrawing(drawing)}
          onClick={() => onConfirm(drawing)}
        >
          {t.signAndSend}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
